// The interface for each device in the Battery object

interface Leds {
  Blue: string;
  Amber: string;
  Green: string;
  Red: string;
}

interface Dc {
  Current: number;
  Voltage: number;
  Power: number;
}

interface Temperatures {
  Cells: {
    Average: number;
    Right: number;
    Left: number;
  };
  State: number;
  Board: number;
  Heating: number;
}

interface IoStatus {
  ConnectedToDcBus: boolean;
  AuxRelayBus: boolean;
  AlarmOutActive: boolean;
  InternalFanActive: boolean;
  RemoteStateActive: boolean;
  VoltMeasurementAllowed: boolean;
  RiscActive: boolean;
}

interface BatteryStrings {
  String1Active: string;
  String2Active: string;
  String3Active: string;
  String4Active: string;
  String5Active: string;
}

export interface Device {
  BatteryDeligreenDataRecord: {
    BatteryCapacity: number;
    BusCurrent: number;
    BusVoltage: number;
    CellVoltage: string;
    FwVersion: number;
    NumberOfCycles: number;
    Power: number;
    RatedCapacity: number;
    ResidualCapacity: number;
    Soc: number;
    Soh: number;

    TemperaturesList: {
      CellTemperature1: number;
      CellTemperature2: number;
      CellTemperature3: number;
      CellTemperature4: number;
      EnvironmentTemperature: number;
      PowerTemperature: number;
    };

    TotalBatteryVoltage: number;
  };

  BatteryDeligreenAlarmRecord: {
    OnOffState: {
      ChargeSwitchState: string;
      CurrentLimitSwitchStat: string;
      DischargeSwitchState: string;
      HeatingSwitchState: string;
    };

    AlarmEvent1: {
      CellVoltageDropoutFault: string;
      ChargeSwitchFault: string;
      CurrentLimitSwitchFault: string;
      CurrentSensorFault: string;
      DischargeSwitchFault: string;
      KeySwitchFault: string;
      TemperatureSensorFault: string;
      VoltageSensorFault: string;
    };

    AlarmEvent2: {
      HighVoltageAlarmForTotalVoltage: string;
      LowVoltageAlarmForTotalVoltage: string;
      MonomerHighVoltageAlarm: string;
      MonomerLowVoltageAlarm: string;
      MonomerOvervoltageProtection: string;
      MonomerUnderVoltageProtection: string;
      OvervoltageProtectionForTotalVoltage: string;
      UnderVoltageProtectionForTotalVoltage: string;
    };

    AlarmEvent3: {
      ChargeHighTemperatureAlarm: string;
      ChargeLowTemperatureAlarm: string;
      ChargeOverTemperatureProtection: string;
      ChargeUnderTemperatureProtection: string;
      DischargeHighTemperatureAlarm: string;
      DischargeLowTemperatureAlarm: string;
      DischargeOverTemperatureProtection: string;
      DischargeUnderTemperatureProtection: string;
    };

    AlarmEvent4: {
      CellLowTemperatureHeating: string;
      EnvironmentHighTemperatureAlarm: string;
      EnvironmentLowTemperatureAlarm: string;
      EnvironmentOverTemperatureProtection: string;
      EnvironmentUnderTemperatureProtection: string;
      PowerHighTemperatureAlarm: string;
      PowerOverTemperatureProtection: string;
    };

    AlarmEvent5: {
      ChargeOverCurrentAlarm: string;
      ChargeOverCurrentProtection: string;
      DischargeOverCurrentAlarm: string;
      DischargeOverCurrentProtection: string;
      OutputShortCircuitLockout: string;
      OutputShortCircuitProtection: string;
      TransientOverCurrentLockout: string;
      TransientOverCurrentProtection: string;
    };

    AlarmEvent6: {
      CellLowVoltageChargingProhibition: string;
      ChargeHighVoltageProtection: string;
      InsideBit: string;
      IntermittentRechargeWaiting: string;
      OutputConnectionFault: string;
      OutputReversePolarityProtection: string;
      ResidualCapacityAlarm: string;
      ResidualCapacityProtection: string;
    };

    AlarmEvent7: {
      AutomaticChargingWaiting: string;
      InsideBit1: string;
      InsideBit2: string;
      InsideBit3: string;
      InsideBit4: string;
      InsideBit5: string;
      InsideBit6: string;
      ManualChargingWaiting: string;
    };

    AlarmEvent8: {
      CurrentCalibrationNotPerformed: string;
      EepStorageFault: string;
      InsideBit1: string;
      InsideBit2: string;
      InsideBit3: string;
      RtcError: string;
      VoltageCalibrationNotPerformed: string;
      ZeroCalibrationNotPerformed: string;
    };

    CellAlarmList: string;
    CellTemperatureAlarm: string;

    SystemState: {
      Charge: string;
      Discharge: string;
      FloatingCharge: string;
      Shutdown: string;
      Standby: string;
    };
  };

  Leds: Leds;
  Eoc: boolean;
  Soc: number;
  SerialNumber: string;
  TimeSinceTOC: string;
  MaxChargePower: number;
  CellsCurrent: number;
  SOCAh: number;
  Dc: Dc;
  FwVersion: string;
  HeatingCurrent: number;
  MaxDischargePower: number;
  Temperatures: Temperatures;
  BusCurrent: number;
  HeatingPower: number;
  IoStatus: IoStatus;
  BatteryStrings: BatteryStrings;
  CalibrationChargeRequested: string;
  Alarms: string;
  Warnings: string;
}

export interface Power {
  Active: number;
  Apparent: number;
  Reactive: number;
}

export interface PvString {
  Power: number;
  Voltage: number;
  Current: number;
}

export interface Line {
  Voltage: number;
  Current: number;
  Phi: number;
  Power: Power;
}

export interface SodioHomeBattery {
  AccumulatedChargeEnergy: number;
  AccumulatedDischargeEnergy: number;
  Current: number;
  DailyChargeEnergy: number;
  DailyDischargeEnergy: number;
  MaxAllowableChargePower: number;
  MaxAllowableDischargePower: number;
  Power: number;
  Soc: number;
  Soh: number;
  Voltage: number;
}

// The interface for the Battery structure, with dynamic keys (Device IDs)
export interface JSONRecordData {
  Battery: {
    Devices: {
      [deviceId: string]: Device; // Device ID as the key
    };

    Dc: {
      Voltage: number;
      Current: number;
      Power: number;
    };

    //For Sodistore Max
    //------------------------------

    Power: number;
    Current: number;
    TemperatureCell1: number;
    Voltage: number;
    //------------------------------
    CurrentMinSoc: number;
    Soc: number;
    Soh: number;
    Temperature: number;
  };

  AcDc: {
    Ac: {
      Frequency: number;
      L1: Line;
      L2: Line;
      L3: Line;
      Power: Power;
    };

    Dc: {
      Voltage: number;
      Current: number;
      Power: number;
    };

    Alarms: string[];
    Warnings: string[];
  };

  AcDcToDcLink: { Power: number };
  PvOnAcGrid: { Power: Power };
  PvOnAcIsland: { Power: Power };
  AcGridToAcIsland: { Power: Power };

  islandBus: {
    AcDc: {
      Ac: {
        L1: Line;
        L2: Line;
        L3: Line;
      };
    };
  };

  Config: {
    BatterySelfDischargePower: number;
    CurtailP: number;
    DayAndTimeForAdditionalCalibration: string;
    DayAndTimeForRepetitiveCalibration: string;
    DownDayAndTimeForAdditionalCalibration: string;
    DownDayAndTimeForRepetitiveCalibration: string;
    DisplayIndividualBatteries: string;
    MaxBatteryDischargingCurrent: number;
    ForceCalibrationChargeState: string;
    ForceCalibrationDischargeState: string;
    GridSetPoint: number;
    HoldSocZone: number;
    MinSoc: number;
    PConstant: number;
    PvInstalledPower: number;

    Devices: {
      BatteryNodes: string;
      BatteryIp: { DeviceState: string };
      GridMeterIp: { DeviceState: string };
      IslandBusLoadMeterIp: { DeviceState: string };
      LoadOnAcGrid: { DeviceState: string };
      LoadOnDc: { DeviceState: string };
      PvOnAcGrid: { DeviceState: string };
      PvOnAcIsland: { DeviceState: string };
      PvOnDc: { DeviceState: string };
      RelaysIp: { DeviceState: string };
      TruConvertAcIp: { DeviceState: string };
      TruConvertDcIp: { DeviceState: string };
      TsRelaysIp: { DeviceState: string };
    };

    //For SodistoerHome
    MaximumChargingCurrent: number;
    MaximumDischargingCurrent: number;
    OperatingPriority: string;
    BatteriesCount: number;
    ClusterNumber: number;
    PvNumber: number;

    //For SodistoerHome-Growatt:
    ControlPermission:boolean;

    //For SodistoerHome-Sinexcel: TimeChargeDischarge mode
    TimeChargeDischargePower?: number;
    TimeChargeDischargeTime?: string;
    TimeChargeDischargeDate?: string;
  };

  DcDc: {
    Dc: {
      Battery: {
        Voltage: number;
        Current: number;
        Power: number;
      };
      Link: {
        Voltage: number;
        Current: number;
        Power: number;
      };
    };

    SystemControl: {
      Alarms: string;
      CommunicationTimeout: string;
      DeviceState: string;
      NumberOfConnectedSlaves: number;
      NumberOfConnectedSubSlaves: number;
      PowerSetPointActivation: string;
      PowerSetPointTrigger: string;
      ReferenceFrame: string;
      ResetAlarmsAndWarnings: string;
      SlaveErrorHandling: string;
      SubSlaveErrorHandling: string;
      SystemConfig: string;
      TargetSlave: number;
      UseSlaveIdForAddressing: string;
      Warnings: string;
    };

    Alarms: string[];
    Warnings: string[];
  };

  EssControl: {
    Mode: string;
    LimitedBy: string;
    PowerCorrection: number;
    PowerSetPoint: number;
  };

  GridMeter: {
    Ac: {
      Frequency: number;
      L1: Line;
      L2: Line;
      L3: Line;
      Power: Power;
    };
    ActivePowerExportT1: number;
    ActivePowerExportT2: number;
    ActivePowerExportT3: number;
    ActivePowerExportT4: number;
    ActivePowerImportT1: number;
    ActivePowerImportT2: number;
    ActivePowerImportT3: number;
    ActivePowerImportT4: number;
  };

  LoadOnAcGrid: { Power: Power };

  LoadOnAcIsland: {
    Ac: {
      Frequency: number;
      L1: Line;
      L2: Line;
      L3: Line;
      Power: Power;
    };
    ActivePowerExportT1: number;
    ActivePowerExportT2: number;
    ActivePowerExportT3: number;
    ActivePowerExportT4: number;
    ActivePowerImportT1: number;
    ActivePowerImportT2: number;
    ActivePowerImportT3: number;
    ActivePowerImportT4: number;
  };

  LoadOnDc: { Power: number };

  PvOnDc: {
    [deviceId: string]: {
      DcWh: number;
      Dc: {
        Voltage: number;
        Current: number;
        Power: number;
        NbrOfStrings: number;
      };
      Strings: {
        [PvId: string]: PvString;
      };
    };
  };

  // For SodistoreHome
  InverterRecord: {
    GridPower:number;
    Battery1Power:number;
    Battery1Soc:number;
    Battery1Soh:number;
    Battery1Voltage:number;
    Battery1Current:number;
    Battery2Power:number;
    Battery2Soc:number;
    Battery2Voltage:number;
    Battery2Current:number;
    Battery2Soh:number;
    PvPower:number;
    ConsumptionPower:number;
  };

  AcDcGrowatt: {
    AcChargeEnable: number;
    ActivePowerPercentDerating: number;
    ActualChargeDischargePowerControlValue: number;
    AlarmMainCode: number;
    AlarmSubCode: number;
    BatteriesRecords: {
      AverageSoc: number;
      AverageSoh: number;
      Batteries: { [deviceId: string]: SodioHomeBattery };
      LowestSoc: number;
      Power: number;
      TotalChargeEnergy: number;
      TotalDischargeEnergy: number;
      TotalMaxCharge: number;
      TotalMaxDischarge: number;
    };

    BatteryChargeCutoffVoltage: number;
    BatteryClusterIndex: number;
    BatteryDischargeCutoffVoltage: number;
    BatteryMaxChargeCurrent: number;
    BatteryMaxChargePower: number;
    BatteryMaxDischargePower: number;
    BatteryMaxdischargeCurrent: number;
    BatteryOperatingMode: string;
    BatteryType: number;
    ChargeCutoffSoc: number;
    ConsumptionPower:number;
    ControlPermession: number;
    DischargeCutoffSoc: number;
    EmsCommunicationFailureTime: number;
    EnableCommand: number;
    EnableEmsCommunicationFailureTime: number;
    EnableSyn: string;
    EnergyToGrid: number;
    EnergyToUser: number;
    FaultMainCode: number;
    FaultSubCode: number;
    Frequency: number;
    GridAbLineVoltage: number;
    GridBcLineVoltage: number;
    GridCaLineVoltage: number;
    InverterActivePower: number;
    InverterReactivePower: number;
    InverterTemperature: number;
    LoadPriorityDischargeCutoffSoc: number;
    MaxActivePower: number;
    MeterPower: number;
    OffGridDischargeCutoffSoc: number;
    OperatingPriority: string;
    PhaseACurrent: number;
    PhaseBCurrent: number;
    PhaseCCurrent: number;
    PowerFactor: number;
    Pv1Current: number;
    Pv1Voltage: number;
    Pv2Current: number;
    Pv2Voltage: number;
    PvInputMaxPower: number;
    RatedPower: number;
    RemoteChargDischargePower: number;
    RemotePowerControl: number;
    SystemDateTime: string;
    SystemOperatingMode: string;
    TotalEnergyToGrid: number;
    TotalEnergyToUser: number;
    TotalPvPower: number;
    VppProtocolVerNumber: number;
  };

  //   DcWh: number;
  //   // NbrOfStrings: number;
  //   Dc: { [deviceId: string]: Dc };
  //   // Dc: {
  //   //   Voltage: number;
  //   //   Current: number;
  //   //   Power: number;
  //   // };
  //   Strings: {
  //     [PvId: string]: PvString;
  //   };
  // };
}

export const parseChunkJson = (
  text: string
): Record<string, JSONRecordData> => {
  const lines = text.split(/\r?\n/).filter((line) => line.length > 0);

  let result: Record<string, any> = {};
  let currentTimestamp = null;

  lines.forEach((line) => {
    const fields = line.split(';');
    if (fields[0] === 'Timestamp') {
      currentTimestamp = fields[1];
      result[currentTimestamp] = {};
    } else if (currentTimestamp) {
      result[currentTimestamp] = JSON.parse(line);
    }
  });

  return result;
};
//--------------------------------------------------------------------------------------------------------------------------------------------------------------------
//--------------------------------------------------------------------------------------------------------------------------------------------------------------------
//--------------------------------------------------------------------------------------------------------------------------------------------------------------------
//--------------------------------------------------------------------------------------------------------------------------------------------------------------------
//--------------------------------------------------------------------------------------------------------------------------------------------------------------------
//                                                                       For CSV manipulation, check the following functions
//--------------------------------------------------------------------------------------------------------------------------------------------------------------------
//--------------------------------------------------------------------------------------------------------------------------------------------------------------------
//--------------------------------------------------------------------------------------------------------------------------------------------------------------------
//--------------------------------------------------------------------------------------------------------------------------------------------------------------------
//
export interface I_CsvEntry {
  value: string | number;
  unit: string;
}

//
// export const parseCsv = (text: string): DataRecord => {
//   const y = text
//     .split(/\r?\n/)
//     .filter((split) => split.length > 0)
//     .map((l) => {
//       return l.split(';');
//     });
//
//   return y
//     .map((fields) => {
//       if (isNaN(Number(fields[1])) || fields[1] === '') {
//         return { [fields[0]]: { value: fields[1], unit: fields[2] } };
//       }
//       return { [fields[0]]: { value: parseFloat(fields[1]), unit: fields[2] } };
//     })
//     .reduce((acc, current) => ({ ...acc, ...current }), {} as DataRecord);
// };
//
// export const parseChunk = (text: string): Record<string, DataRecord> => {
//   const lines = text.split(/\r?\n/).filter((line) => line.length > 0);
//
//   let result: Record<string, DataRecord> = {};
//   let currentTimestamp = null;
//
//   lines.forEach((line) => {
//     const fields = line.split(';');
//     if (fields[0] === 'Timestamp') {
//       currentTimestamp = fields[1];
//       result[currentTimestamp] = {};
//     } else if (currentTimestamp) {
//       let key = fields[0];
//       let value = fields[1];
//       let unit = fields[2];
//
//       if (isNaN(Number(value)) || value === '') {
//         result[currentTimestamp][key] = { value: value, unit: unit };
//       } else {
//         result[currentTimestamp][key] = {
//           value: parseFloat(value),
//           unit: unit
//         };
//       }
//     }
//   });
//   return result;
// };
//
export interface I_BoxDataValue {
  unit: string;
  value: string | number;
}

export type ConfigurationValues = {
  minimumSoC: string | number;
  gridSetPoint: number;
  calibrationChargeState: number;
  calibrationChargeDate: Date | null;
  calibrationDischargeState: number;
  calibrationDischargeDate: Date | null;

  //For sodistoreHome
  maximumDischargingCurrent: number;
  maximumChargingCurrent: number;
  operatingPriority: number;
  batteriesCount: number;
  clusterNumber: number;
  PvNumber: number;

  //For sodistoreHome-Growatt:
  controlPermission:boolean;

  // For sodistoreHome-Sinexcel: TimeChargeDischarge mode
  timeChargeDischargePower?: number;
  timeChargeDischargeTime?: Date | string;
  timeChargeDischargeDate?: Date | string;
};
//
// export interface Pv {
//   PvId: number;
//   Power: I_BoxDataValue;
//   Voltage: I_BoxDataValue;
//   Current: I_BoxDataValue;
// }
//
// export interface Battery {
//   BatteryId: number;
//   FwVersion: I_BoxDataValue;
//   Power: I_BoxDataValue;
//   Voltage: I_BoxDataValue;
//   Soc: I_BoxDataValue;
//   AverageTemperature: I_BoxDataValue;
//   Warnings: I_BoxDataValue;
//   Alarms: I_BoxDataValue;
//   Current: I_BoxDataValue;
//   BusCurrent: I_BoxDataValue;
//   CellsCurrent: I_BoxDataValue;
//   HeatingCurrent: I_BoxDataValue;
//   HeatingPower: I_BoxDataValue;
//   SOCAh: I_BoxDataValue;
//   BlueLeds: I_BoxDataValue;
//   RedLeds: I_BoxDataValue;
//   GreenLeds: I_BoxDataValue;
//   AmberLeds: I_BoxDataValue;
//   HeatingTemperature: I_BoxDataValue;
//   BoardTemperature: I_BoxDataValue;
//   LeftCellsTemperature: I_BoxDataValue;
//   RightCellsTemperature: I_BoxDataValue;
//   StateTemperature: I_BoxDataValue;
//   String1Active: I_BoxDataValue;
//   String2Active: I_BoxDataValue;
//   String3Active: I_BoxDataValue;
//   String4Active: I_BoxDataValue;
//   String5Active: I_BoxDataValue;
//   ConnectedToDcBus: I_BoxDataValue;
//   AlarmOutActive: I_BoxDataValue;
//   InternalFanActive: I_BoxDataValue;
//   VoltMeasurementAllowed: I_BoxDataValue;
//   AuxRelayBus: I_BoxDataValue;
//   RemoteStateActive: I_BoxDataValue;
//   RiscActive: I_BoxDataValue;
//   Eoc: I_BoxDataValue;
//   SerialNumber: I_BoxDataValue;
//   TimeSinceTOC: I_BoxDataValue;
//   CalibrationChargeRequested: I_BoxDataValue;
//   MaxChargePower: I_BoxDataValue;
//   MaxDischargePower: I_BoxDataValue;
// }
//
// const PvKeys = ['PvId', 'Power', 'Voltage', 'Current'];
//
// const BatteryKeys = [
//   'BatteryId',
//   'FwVersion',
//   'Power',
//   'Voltage',
//   'Soc',
//   'AverageTemperature',
//   'Warnings',
//   'Alarms',
//   'Current',
//   'BusCurrent',
//   'CellsCurrent',
//   'HeatingCurrent',
//   'HeatingPower',
//   'SOCAh',
//   'BlueLeds',
//   'RedLeds',
//   'GreenLeds',
//   'AmberLeds',
//   'HeatingTemperature',
//   'BoardTemperature',
//   'LeftCellsTemperature',
//   'RightCellsTemperature',
//   'StateTemperature',
//   'String1Active',
//   'String2Active',
//   'String3Active',
//   'String4Active',
//   'String5Active',
//   'ConnectedToDcBus',
//   'AlarmOutActive',
//   'InternalFanActive',
//   'VoltMeasurementAllowed',
//   'AuxRelayBus',
//   'RemoteStateActive',
//   'RiscActive',
//   'Eoc',
//   'SerialNumber',
//   'TimeSinceTOC',
//   'CalibrationChargeRequested',
//   'MaxChargePower',
//   'MaxDischargePower'
// ];
//
// export type TopologyValues = {
//   gridBox: I_BoxDataValue[];
//   pvOnAcGridBox: I_BoxDataValue[];
//   loadOnAcGridBox: I_BoxDataValue[];
//   pvOnIslandBusBox: I_BoxDataValue[];
//   loadOnIslandBusBox: I_BoxDataValue[];
//   pvOnDcBox: I_BoxDataValue[];
//   loadOnDcBox: I_BoxDataValue[];
//   batteryBox: I_BoxDataValue[];
//
//   grid: I_BoxDataValue[];
//   gridToAcInConnection: I_BoxDataValue[];
//   gridBus: I_BoxDataValue[];
//   islandBus: I_BoxDataValue[];
//   dcBus: I_BoxDataValue[];
//   dcBusToDcDcConnection: I_BoxDataValue[];
//   dcDCToBatteryConnection: I_BoxDataValue[];
//   battery: I_BoxDataValue[];
//   dcBusToLoadOnDcConnection: I_BoxDataValue[];
//   islandBusToLoadOnIslandBusConnection: I_BoxDataValue[];
//   gridBusToPvOnGridbusConnection: I_BoxDataValue[];
//   gridBusToLoadOnGridBusConnection: I_BoxDataValue[];
//   inverter: I_BoxDataValue[];
//   dcDc: I_BoxDataValue[];
//   islandBusToInverter: I_BoxDataValue[];
//   inverterToDcBus: I_BoxDataValue[];
//   gridBusToIslandBusConnection: I_BoxDataValue[];
//   pvOnDcBusToDcBusConnection: I_BoxDataValue[];
//   pvOnIslandBusToIslandBusConnection: I_BoxDataValue[];
//   minimumSoC: I_BoxDataValue[];
//   installedDcDcPower: I_BoxDataValue[];
//   gridSetPoint: I_BoxDataValue[];
//   maximumDischargePower: I_BoxDataValue[];
//   DcDcNum: I_BoxDataValue[];
//   calibrationChargeState: I_BoxDataValue[];
//   mode: I_BoxDataValue[];
//   repetitiveCalibrationChargeDate: I_BoxDataValue[];
//   additionalCalibrationChargeDate: I_BoxDataValue[];
//
//   batteryView: Battery[];
//
//   pvView: Pv[];
// };
// type TopologyPaths = { [key in keyof TopologyValues]: string[] };
//
// const batteryIds = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];
//
// const pvIds = [
//   1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22,
//   23, 24, 25, 26, 27, 28, 29, 30
// ];
//
// const PvPaths = [
//   '/PvOnDc/Strings/%id%/Power',
//   '/PvOnDc/Strings/%id%/Voltage',
//   '/PvOnDc/Strings/%id%/Current'
// ];
//
// const batteryPaths = [
//   '/Battery/Devices/%id%/FwVersion',
//   '/Battery/Devices/%id%/Dc/Power',
//   '/Battery/Devices/%id%/Dc/Voltage',
//   '/Battery/Devices/%id%/Soc',
//   '/Battery/Devices/%id%/Temperatures/Cells/Average',
//   '/Battery/Devices/%id%/Warnings',
//   '/Battery/Devices/%id%/Alarms',
//
//   '/Battery/Devices/%id%/Dc/Current',
//   '/Battery/Devices/%id%/BusCurrent',
//   '/Battery/Devices/%id%/CellsCurrent',
//   '/Battery/Devices/%id%/HeatingCurrent',
//   '/Battery/Devices/%id%/HeatingPower',
//   '/Battery/Devices/%id%/SOCAh',
//
//   '/Battery/Devices/%id%/Leds/Blue',
//   '/Battery/Devices/%id%/Leds/Red',
//   '/Battery/Devices/%id%/Leds/Green',
//   '/Battery/Devices/%id%/Leds/Amber',
//
//   '/Battery/Devices/%id%/Temperatures/Heating',
//   '/Battery/Devices/%id%/Temperatures/Board',
//   '/Battery/Devices/%id%/Temperatures/Cells/Left',
//   '/Battery/Devices/%id%/Temperatures/Cells/Right',
//   '/Battery/Devices/%id%/Temperatures/State',
//
//   '/Battery/Devices/%id%/BatteryStrings/String1Active',
//   '/Battery/Devices/%id%/BatteryStrings/String2Active',
//   '/Battery/Devices/%id%/BatteryStrings/String3Active',
//   '/Battery/Devices/%id%/BatteryStrings/String4Active',
//   '/Battery/Devices/%id%/BatteryStrings/String5Active',
//
//   '/Battery/Devices/%id%/IoStatus/ConnectedToDcBus',
//   '/Battery/Devices/%id%/IoStatus/AlarmOutActive',
//   '/Battery/Devices/%id%/IoStatus/InternalFanActive',
//   '/Battery/Devices/%id%/IoStatus/VoltMeasurementAllowed',
//   '/Battery/Devices/%id%/IoStatus/AuxRelayBus',
//   '/Battery/Devices/%id%/IoStatus/RemoteStateActive',
//   '/Battery/Devices/%id%/IoStatus/RiscActive',
//
//   '/Battery/Devices/%id%/Eoc',
//   '/Battery/Devices/%id%/SerialNumber',
//   '/Battery/Devices/%id%/TimeSinceTOC',
//   '/Battery/Devices/%id%/CalibrationChargeRequested',
//   '/Battery/Devices/%id%/MaxChargePower',
//   '/Battery/Devices/%id%/MaxDischargePower'
// ];
//
// export const topologyPaths: TopologyPaths = {
//   gridBox: ['/Config/Devices/GridMeterIp/DeviceState'],
//   pvOnAcGridBox: ['/Config/Devices/PvOnAcGrid/DeviceState'],
//   loadOnAcGridBox: ['/Config/Devices/LoadOnAcGrid/DeviceState'],
//   pvOnIslandBusBox: ['/Config/Devices/PvOnAcIsland/DeviceState'],
//   loadOnIslandBusBox: ['/Config/Devices/IslandBusLoadMeterIp/DeviceState'],
//   pvOnDcBox: ['/Config/Devices/PvOnDc/DeviceState'],
//   loadOnDcBox: ['/Config/Devices/LoadOnDc/DeviceState'],
//   batteryBox: ['/Config/Devices/BatteryIp/DeviceState'],
//
//   grid: [
//     '/GridMeter/Ac/L1/Power/Active',
//     '/GridMeter/Ac/L2/Power/Active',
//     '/GridMeter/Ac/L3/Power/Active'
//   ],
//   gridToAcInConnection: ['/GridMeter/Ac/Power/Active'],
//
//   gridBus: [
//     '/GridMeter/Ac/L1/Voltage',
//     '/GridMeter/Ac/L2/Voltage',
//     '/GridMeter/Ac/L3/Voltage'
//   ],
//   gridBusToPvOnGridbusConnection: ['/PvOnAcGrid/Power/Active'],
//
//   gridBusToLoadOnGridBusConnection: ['/LoadOnAcGrid/Power/Active'],
//   gridBusToIslandBusConnection: ['/AcGridToAcIsland/Power/Active'],
//
//   islandBus: [
//     '/AcDc/Ac/L1/Voltage',
//     '/AcDc/Ac/L2/Voltage',
//     '/AcDc/Ac/L3/Voltage'
//   ],
//   islandBusToLoadOnIslandBusConnection: ['/LoadOnAcIsland/Ac/Power/Active'],
//   islandBusToInverter: ['/AcDc/Dc/Power'],
//   pvOnIslandBusToIslandBusConnection: ['/PvOnAcIsland/Power/Active'],
//
//   inverter: [
//     '/AcDc/Ac/L1/Power/Active',
//     '/AcDc/Ac/L2/Power/Active',
//     '/AcDc/Ac/L3/Power/Active',
//     '/AcDc/Ac/L4/Power/Active'
//   ],
//   inverterToDcBus: ['/AcDcToDcLink/Power'],
//
//   dcBus: ['/DcDc/Dc/Link/Voltage'],
//   dcBusToDcDcConnection: ['/DcDc/Dc/Link/Power'],
//   pvOnDcBusToDcBusConnection: ['/PvOnDc/Dc/Power'],
//   dcBusToLoadOnDcConnection: ['/LoadOnDc/Power'],
//
//   dcDc: ['/DcDc/Dc/Battery/Voltage'],
//   dcDCToBatteryConnection: ['/Battery/Dc/Power'],
//
//   battery: [
//     '/Battery/Soc',
//     '/Battery/Dc/Voltage',
//     '/Battery/Dc/Current',
//     '/Battery/Temperature',
//     '/Battery/Devices/1/Dc/Voltage',
//     '/Battery/Devices/2/Dc/Voltage',
//     '/Battery/Devices/3/Dc/Voltage',
//     '/Battery/Devices/4/Dc/Voltage',
//     '/Battery/Devices/5/Dc/Voltage',
//     '/Battery/Devices/6/Dc/Voltage',
//     '/Battery/Devices/7/Dc/Voltage',
//     '/Battery/Devices/8/Dc/Voltage',
//     '/Battery/Devices/9/Dc/Voltage',
//     '/Battery/Devices/10/Dc/Voltage'
//   ],
//
//   batteryView: batteryIds.flatMap((id) =>
//     batteryPaths.map((path) => path.replace('%id%', id.toString()))
//   ),
//
//   pvView: pvIds.flatMap((id) =>
//     PvPaths.map((path) => path.replace('%id%', id.toString()))
//   ),
//
//   minimumSoC: ['/Config/MinSoc'],
//   installedDcDcPower: ['/DcDc/SystemControl/NumberOfConnectedSlaves'],
//   gridSetPoint: ['/Config/GridSetPoint'],
//   maximumDischargePower: ['/Config/MaxBatteryDischargingCurrent'],
//   DcDcNum: ['/DcDc/SystemControl/NumberOfConnectedSlaves'],
//   calibrationChargeState: ['/Config/ForceCalibrationChargeState'],
//   mode: ['/EssControl/Mode'],
//   repetitiveCalibrationChargeDate: [
//     '/Config/DayAndTimeForRepetitiveCalibration'
//   ],
//   additionalCalibrationChargeDate: [
//     '/Config/DayAndTimeForAdditionalCalibration'
//   ]
// };
//
// //We are using the function every time we fetch the data from S3 (every 2 seconds).
// //The data is of the following form: TopologyValues
// //key: I_BoxDataValue[] ==> key: [{unit:'',value:''},{unit:'',value:''},...]
// //battery_view: [ {"Battery_id": 2,'FwVersion': {'unit':,'value':}},
// //                {"Battery_id": 4,'FwVersion': {'unit':,'value':}}
// //]
// //For batteries, we follow a different approach. We define a key battery_view that is of type Battery[]
//
// export const extractValues = (
//   timeSeriesData: DataPoint
// ): TopologyValues | null => {
//   const extractedValues: TopologyValues = {} as TopologyValues;
//   for (const topologyKey of Object.keys(topologyPaths)) {
//     //Each topologykey may have more than one paths (for example inverter)
//     const paths = topologyPaths[topologyKey];
//     let topologyValues: { unit: string; value: string | number }[] = [];
//
//     if (topologyKey === 'pvView') {
//       extractedValues[topologyKey] = [];
//       let pv_index = 0;
//       let pathIndex = 0;
//
//       while (pathIndex < paths.length) {
//         let pv = {};
//         let existingKeys = 0;
//
//         //We prepare a pv object for each node. We extract the number of nodes from the  '/PvOnDc/NbrOfStrings' path.
//         //PvKeys[0] is the pv id.
//         pv[PvKeys[0]] = pv_index;
//         //Then, search all the remaining battery keys
//         for (let i = 1; i < PvKeys.length; i++) {
//           const path = paths[pathIndex];
//           if (timeSeriesData.value.hasOwnProperty(path)) {
//             existingKeys++;
//
//             pv[PvKeys[i]] = {
//               unit: timeSeriesData.value[path].unit.includes('~')
//                 ? timeSeriesData.value[path].unit.replace('~', '')
//                 : timeSeriesData.value[path].unit,
//               value:
//                 typeof timeSeriesData.value[path].value === 'string'
//                   ? timeSeriesData.value[path].value
//                   : Number(timeSeriesData.value[path].value).toFixed(1)
//             };
//           }
//           pathIndex++;
//         }
//         pv_index++;
//         if (existingKeys > 0) {
//           extractedValues[topologyKey].push(pv as Pv);
//         }
//       }
//     } else if (topologyKey === 'batteryView') {
//       extractedValues[topologyKey] = [];
//       const node_ids_from_csv = timeSeriesData.value[
//         '/Config/Devices/BatteryNodes'
//       ].value
//         .toString()
//         .split(',');
//
//       let battery_index = 0;
//       let pathIndex = 0;
//
//       while (pathIndex < paths.length) {
//         let battery = {};
//         let existingKeys = 0;
//
//         //We prepare a battery object for each node. We extract the nodes from the  '/Config/Devices/BatteryNodes' path. For example, nodes can be [2,4,5,6] (one is missing)
//         //BatteryKeys[0] is the battery id. We set the battery id to the corresponding node id.
//         battery[BatteryKeys[0]] = node_ids_from_csv[battery_index];
//         //Then, search all the remaining battery keys
//         for (let i = 1; i < BatteryKeys.length; i++) {
//           const path = paths[pathIndex];
//           if (timeSeriesData.value.hasOwnProperty(path)) {
//             existingKeys++;
//
//             battery[BatteryKeys[i]] = {
//               unit: timeSeriesData.value[path].unit.includes('~')
//                 ? timeSeriesData.value[path].unit.replace('~', '')
//                 : timeSeriesData.value[path].unit,
//               value:
//                 typeof timeSeriesData.value[path].value === 'string'
//                   ? timeSeriesData.value[path].value
//                   : Number(timeSeriesData.value[path].value).toFixed(1)
//             };
//           }
//           pathIndex++;
//         }
//         battery_index++;
//         if (existingKeys > 0) {
//           extractedValues[topologyKey].push(battery as Battery);
//         }
//       }
//     } else {
//       // Check if any of the specified paths exist in the dataRecord
//       for (const path of paths) {
//         if (timeSeriesData.value.hasOwnProperty(path)) {
//           topologyValues.push({
//             unit: timeSeriesData.value[path].unit.includes('~')
//               ? timeSeriesData.value[path].unit.replace('~', '')
//               : timeSeriesData.value[path].unit,
//             value: timeSeriesData.value[path].value
//           });
//         }
//       }
//       if (topologyValues.length > 0) {
//         extractedValues[topologyKey] = topologyValues;
//       }
//     }
//   }
//
//   return extractedValues;
// };

export const getHighestConnectionValue = (values: JSONRecordData) => {
  // Define the paths you want to check
  const paths = [
    'GridMeter.Ac.Power.Active',
    'PvOnAcGrid.Power.Active',
    'AcGridToAcIsland.Power.Active',
    'LoadOnAcGrid.Power.Active',
    'PvOnAcIsland.Power.Active',
    'AcDc.Dc.Power',
    'LoadOnAcIsland.Ac.Power.Active',
    'AcDcToDcLink.Power',
    'PvOnDc.Dc.Power',
    'DcDc.Dc.Link.Power',
    'LoadOnDc.Power',
    'Battery.Dc.Power',
    'AcDcGrowatt.MeterPower',
    'AcDcGrowatt.TotalPvPower',
    'AcDcGrowatt.BatteriesRecords.Power',
    'AcDcGrowatt.BatteriesRecords.TotalChargeEnergy',
    'AcDcGrowatt.ConsumptionPower',
    'InverterRecord.GridPower',
    'InverterRecord.PvPower',
    'InverterRecord.Battery1Power',
    'InverterRecord.Battery2Power',
    'InverterRecord.ConsumptionPower'
  ];

  // Helper function to safely get a value from a nested path
  const getValueAtPath = (obj: any, path: string) => {
    return path.split('.').reduce((acc, part) => {
      return acc && acc[part] !== undefined ? acc[part] : null;
    }, obj);
  };

  // Find the maximum value by iterating over the paths
  return paths.reduce((maxValue, path) => {
    // Safely get the value at the path
    const value = getValueAtPath(values, path);

    // If the value exists and is a number, check if it's greater than the current max
    if (value !== null) {
      return Math.max(maxValue, Math.abs(value));
    }
    return maxValue;
  }, 0); // Initialize maxValue to 0
};

export const getAmount = (highestConnectionValue: number, value: number) => {
  return parseFloat((Math.abs(value) / highestConnectionValue).toFixed(1));
};
